import {
	CreateDrawerFormProps,
	FormDrawer,
	FormDrawerContent,
	FormDrawerContentProps,
	FormFetchDrawer,
	Id,
	MultiSelectField,
	TextField,
	UpdateDrawerFormProps,
} from "@dgs/core";
import { TeamShowDto } from "apiGen";
import { useFormikContext } from "formik";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { EmployeeMultiSelectField } from "~root/employees/EmployeeSelectField";
import { teamService } from "~shared/api/team/team_service";

export type ITeamFormState = Omit<TeamShowDto, "id">;

const useValidate = () => {
	const { t } = useTranslation();

	return (values: ITeamFormState) => {
		const errors: { [K in keyof ITeamFormState]?: any } = {};

		if (!values.name) {
			errors.name = t("Field is required");
		}
		if (!values.employees.length) {
			errors.employees = t("Field is required");
		}
		return errors;
	};
};

const TeamDrawerContent: FC<FormDrawerContentProps> = (props) => {
	const { t } = useTranslation();
	const { values, setFieldValue } = useFormikContext<ITeamFormState>();

	return (
		<FormDrawerContent {...props}>
			<TextField name="name" label={t("Name")} />
			<EmployeeMultiSelectField
				name="employees"
				label={t("Employees")}
				onChange={(e) => {
					const idsToRemove = values.leaders.reduce((idsToRemove, leader) => {
						if (!e.target.value.find((x) => leader.id === x.id)) {
							return [...idsToRemove, leader.id];
						}
						return idsToRemove;
					}, [] as Id[]);
					void setFieldValue(
						"leaders",
						values.leaders.filter((leader) => !idsToRemove.includes(leader.id)),
					);
				}}
			/>
			<MultiSelectField
				name="leaders"
				label={t("Team leaders")}
				options={values.employees}
				getLabel={(x) => `${x.firstName} ${x.lastName}`}
			/>
		</FormDrawerContent>
	);
};

export const TeamUpdateDrawer: FC<UpdateDrawerFormProps<ITeamFormState>> = (drawerProps) => {
	const { t } = useTranslation();
	const validate = useValidate();

	return (
		<FormFetchDrawer
			{...drawerProps}
			validate={validate}
			title={t("Edit team")}
			queryFn={() => teamService.show(drawerProps.id).then((x) => x.data.data)}
			queryKey={teamService.showKey(drawerProps.id)}
			renderSuccess={(props) => <TeamDrawerContent {...props} />}
		/>
	);
};

export const TeamCreateDrawer: FC<CreateDrawerFormProps<ITeamFormState>> = (drawerProps) => {
	const { t } = useTranslation();
	const validate = useValidate();
	const initialValues: ITeamFormState = {
		name: "",
		employees: [],
		leaders: [],
	};

	return (
		<FormDrawer
			{...drawerProps}
			validate={validate}
			title={t("Create team")}
			initialValues={initialValues}
			renderForm={(props) => <TeamDrawerContent {...props} />}
		/>
	);
};
